import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Servicio del módulo Cartera Castigada (Pérdidas Incobrables).
 * Consume /api/cartera-castigada del backend.
 */
@Injectable({ providedIn: 'root' })
export class CarteraCastigadaService {
  private base: string;

  constructor(private http: HttpClient) {
    this.base = `${environment.BASE_LA}/api/cartera-castigada`;
  }

  resumen = async (lote?: string) => {
    let params = new HttpParams();
    if (lote) params = params.set('lote', lote);
    return lastValueFrom(this.http.get<any>(`${this.base}/resumen`, { params }));
  };

  clientes = async (opts: { page?: number; limit?: number; q?: string; estado?: string; lote?: string; estatus_credito?: string } = {}) => {
    let params = new HttpParams()
      .set('page', String(opts.page || 1))
      .set('limit', String(opts.limit || 20));
    if (opts.q) params = params.set('q', opts.q);
    if (opts.estado) params = params.set('estado', opts.estado);
    if (opts.lote) params = params.set('lote', opts.lote);
    if (opts.estatus_credito) params = params.set('estatus_credito', opts.estatus_credito);
    return lastValueFrom(this.http.get<any>(`${this.base}/clientes`, { params }));
  };

  cliente = async (personId: string, lote?: string) => {
    let params = new HttpParams();
    if (lote) params = params.set('lote', lote);
    return lastValueFrom(this.http.get<any>(`${this.base}/cliente/${personId}`, { params }));
  };

  // ── Lotes: listado de lotes ya creados ──
  lotes = async () =>
    lastValueFrom(this.http.get<any>(`${this.base}/lotes`));

  loteActivo = async () =>
    lastValueFrom(this.http.get<any>(`${this.base}/lote/activo`));

  simular = async (corte: string, force = false) =>
    lastValueFrom(this.http.post<any>(`${this.base}/simular`, { corte, force }));

  // ── Lotes: marcaje real (crea el lote) y estado del marcaje ──
  crearLote = async (body: { corte: string; lote?: string }) =>
    lastValueFrom(this.http.post<any>(`${this.base}/simular/crear`, body));

  loteEstado = async (corte: string) => {
    const params = new HttpParams().set('corte', corte);
    return lastValueFrom(this.http.get<any>(`${this.base}/lote/estado`, { params }));
  };

  simularEstado = async (corte: string) => {
    const params = new HttpParams().set('corte', corte);
    return lastValueFrom(this.http.get<any>(`${this.base}/simular/estado`, { params }));
  };

  // ── Lotes: listado paginado de clientes simulados ──
  simularClientes = async (opts: { corte: string; page?: number; limit?: number; q?: string }) => {
    let params = new HttpParams()
      .set('corte', opts.corte)
      .set('page', String(opts.page || 1))
      .set('limit', String(opts.limit || 20));
    if (opts.q) params = params.set('q', opts.q);
    return lastValueFrom(this.http.get<any>(`${this.base}/simular/clientes`, { params }));
  };

  // ── Lotes: descarga de Excel de la simulación ──
  simularExport = async (corte: string) => {
    const params = new HttpParams().set('corte', corte);
    return lastValueFrom(
      this.http.get(`${this.base}/simular/export`, { params, responseType: 'blob' })
    );
  };

  // ── Cuentas contables ──
  cuentasList = async () =>
    lastValueFrom(this.http.get<any>(`${this.base}/cuentas`));

  cuentasOpciones = async () =>
    lastValueFrom(this.http.get<any>(`${this.base}/cuentas/opciones`));

  cuentaCrear = async (body: any) =>
    lastValueFrom(this.http.post<any>(`${this.base}/cuentas`, body));

  cuentaActualizar = async (id: string, body: any) =>
    lastValueFrom(this.http.put<any>(`${this.base}/cuentas/${id}`, body));

  cuentaAsociar = async (id: string, body: any) =>
    lastValueFrom(this.http.post<any>(`${this.base}/cuentas/${id}/asociar`, body));
}
